import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import LinearProgress from 'housecall-ui/dist/DesignSystem/Feedback/Progress/LinearProgress';
import Autocomplete, {
  AutocompleteRenderInputParams,
} from 'housecall-ui/dist/DesignSystem/Inputs/AutoComplete/Autocomplete';
import createFilterOptions from 'housecall-ui/dist/DesignSystem/Inputs/AutoComplete/createFilterOptions';
import type { FilterOptionsState, Value } from 'housecall-ui/dist/DesignSystem/Inputs/AutoComplete/useAutocomplete';
import TextField from 'housecall-ui/dist/DesignSystem/Inputs/TextFields/TextField';
import { noop } from 'lodash-es';
import { CallTrackingNumber } from 'resources/CallTracking';

import { Lead } from 'components/CustomerIntake/types/lead';

import CallTrackingNumbersApi, { CallTrackingResponse } from '../../../Resources/CallTracking/CallTrackingNumbersApi';
import LeadSourcesApi from '../../../Resources/LeadSources/Api';

import { SEARCH_DEBOUNCE_TIME } from './Constants';
import CreateLeadSourceDialog from './CreateLeadSourceDialog';

const filter = createFilterOptions<LeadSource>();

export type LeadSource = {
  discarded_at?: null | string;
  is_system?: boolean;
  name: string;
  uuid?: string;
  inputValue?: string;
};

type LeadSourceInputProps = Omit<AutocompleteRenderInputParams, 'fullWidth'> & {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const LeadSourceInput = (props: LeadSourceInputProps) => (
  <TextField fullWidth label="Lead source" data-testid="lead-sources-autocomplete" {...props} />
);

const LeadSourcesAutocomplete = ({
  value,
  onChange = noop,
  onBlur = noop,
  input,
  trackInteract = () => ({ type: 'blank' }),
  trackSave = () => ({ type: 'blank' }),
  trackCleared = () => ({ type: 'blank' }),
  getOptionDisabled = () => false,
  renderOption = (option: LeadSource) => option.name,
  disableLeadSourcesInUse = false,
  ...props
}: LeadSourcesAutocompleteProps) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [inputText, setInputText] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<LeadSource[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newLeadSource, setNewLeadSource] = useState<{ name: string }>({ name: '' });
  const loading = open && options.length === 0;

  useEffect(() => {
    let active = true;
    if (!loading) {
      return undefined;
    }

    const leadSourceParams = {
      page_size: 200,
      sort_direction: 'asc',
      sort_column: 'name',
      q: searchQuery,
    };
    const fetchedLeadSources = LeadSourcesApi.index(leadSourceParams);
    const api_calls: Array<Promise<AxiosResponse<unknown>>> = [fetchedLeadSources];
    if (disableLeadSourcesInUse) {
      const callTrackingParams = {
        pageSize: 200,
        page: 1,
        sortDirection: 'asc',
        sortColumn: 'name',
      };
      const fetchCallTrackingNumbers = CallTrackingNumbersApi.index(callTrackingParams);
      api_calls.push(fetchCallTrackingNumbers);
    }

    Promise.all(api_calls).then(([leadSourcesResponse, callTrackingNumbersResponse]) => {
      if (active) {
        const leadSources = leadSourcesResponse?.data as { data: LeadSource[] };
        let modifiedOptions = leadSources.data;
        if (disableLeadSourcesInUse) {
          const callTrackingNumbers = callTrackingNumbersResponse?.data as CallTrackingResponse;
          modifiedOptions = leadSources.data.map((leadSource: LeadSource) => {
            const matchingLeadSource = (callTrackingNumber: CallTrackingNumber) =>
              callTrackingNumber?.lead_source?.data.uuid === leadSource.uuid;
            const hasCallTrackingNumber = callTrackingNumbers.data.some(matchingLeadSource);
            return { ...leadSource, hasCallTrackingNumber };
          });
        }
        setOptions(modifiedOptions);
      }
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableLeadSourcesInUse, loading]);

  const getOptionLabel = (option: LeadSource | string) => {
    if (typeof option === 'string') return option;
    if (typeof option.name === 'undefined') return '';
    return option.name;
  };

  const handleChange = (_: unknown, newValue?: Value<LeadSource, false, false, true>) => {
    const processChange = input?.onChange || onChange;
    if (typeof newValue === 'string') {
      setDialogOpen(true);
      setNewLeadSource({ name: newValue });
    } else if (newValue?.inputValue) {
      setDialogOpen(true);
      setNewLeadSource({ name: newValue.inputValue });
    } else {
      processChange(newValue || { name: '' });
    }

    if (newValue === null) {
      dispatch(trackCleared());
    }
  };

  const handleInputChange = ({ target: { value: v = '' } = {} }) => {
    setInputText(v);
  };

  useDebouncedEffect(
    () => {
      setOptions([]);
      setSearchQuery(inputText);
    },
    SEARCH_DEBOUNCE_TIME,
    [inputText]
  );

  const onSave = (leadSource: Lead & { inputValue: string }) => {
    handleChange(null, leadSource);
    dispatch(trackSave());
    setDialogOpen(false);
    setOptions([]);
    setNewLeadSource({ name: '' });
  };

  const onCancel = () => {
    handleChange(null);
    setDialogOpen(false);
    setNewLeadSource({ name: '' });
  };

  const handleBlur = () => {
    onBlur(newLeadSource);
  };

  const filterOptions = (optionsToFilter: LeadSource[], params: FilterOptionsState<LeadSource>) => {
    const filtered = filter(optionsToFilter, params);

    if (params.inputValue !== '') {
      filtered.push({
        inputValue: params.inputValue,
        name: `Add "${params.inputValue}"`,
      });
    }

    return filtered;
  };

  const onInteract = () => {
    setOpen(true);
    if (!options.length) {
      dispatch(trackInteract());
    }
  };

  return (
    <>
      <Autocomplete
        open={open}
        onOpen={onInteract}
        onClose={() => {
          setOpen(false);
        }}
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={options}
        getOptionLabel={getOptionLabel}
        value={value}
        onChange={handleChange}
        filterOptions={filterOptions}
        onBlur={handleBlur}
        getOptionDisabled={getOptionDisabled}
        renderInput={(inputProps) => (
          <LeadSourceInput {...inputProps} {...props} onChange={(e) => handleInputChange(e)} />
        )}
        renderOption={(option: LeadSource) => renderOption(option)}
      />
      {loading && <LinearProgress />}
      <CreateLeadSourceDialog
        open={dialogOpen}
        onCancel={onCancel}
        onSave={onSave}
        leadSourceName={newLeadSource?.name}
      />
    </>
  );
};

type LeadSourcesAutocompleteProps = {
  input?: { onChange: () => unknown };
  value: { name: string } | null;
  onChange?: (lead: (Lead & { inputValue: string }) | { name: string }) => void;
  onBlur?: (lead: { name: string }) => void;
  trackSave?: () => unknown;
  trackInteract?: () => unknown;
  trackCleared?: () => unknown;
  getOptionDisabled?: (lead: LeadSource) => boolean;
  renderOption?: (lead: LeadSource) => string;
  disableLeadSourcesInUse?: boolean;
  autoFocus?: boolean;
};

export default LeadSourcesAutocomplete;
